// src/logger.ts
var logger = {
  info(message, ...args) {
    console.log(`[Socket] \u2139\uFE0F ${message}`, ...args);
  },
  error(message, error) {
    console.error(`[Socket] \u274C ${message}`, error || "");
  },
  debug(message, ...args) {
    console.log(`[Socket] \u{1F50D} ${message}`, ...args);
  },
  warn(message, ...args) {
    console.warn(`[Socket] \u26A0\uFE0F ${message}`, ...args);
  },
  success(message, ...args) {
    console.log(`[Socket] \u2705 ${message}`, ...args);
  }
};

// src/socket.ts
import { z as z2 } from "zod";

// src/event-emitter.ts
import { z } from "zod";
var EventEmitter = class {
  eventHandlers = /* @__PURE__ */ new Map();
  ws;
  incomingSchema;
  outgoingSchema;
  constructor(ws, schemas) {
    const { incomingSchema, outgoingSchema } = schemas;
    this.ws = ws;
    this.incomingSchema = incomingSchema;
    this.outgoingSchema = outgoingSchema;
  }
  emit(event, data) {
    if (this.ws.readyState !== WebSocket.OPEN) {
      logger.warn("WebSocket is not in OPEN state. Message not sent.");
      return false;
    }
    if (this.outgoingSchema) {
      try {
        this.outgoingSchema.parse(data);
      } catch (err) {
        this.handleSchemaMismatch(event, data, err);
        return false;
      }
    }
    this.ws.send(JSON.stringify([event, data]));
    return true;
  }
  handleSchemaMismatch(event, data, err) {
    if (err instanceof z.ZodError) {
      logger.error(`Invalid outgoing event data for "${event}":`, {
        errors: err.errors.map((e) => `${e.path.join(".")}: ${e.message}`).join(", "),
        data: JSON.stringify(data, null, 2)
      });
    } else {
      logger.error(`Error validating outgoing event "${event}":`, err);
    }
  }
  handleEvent(eventName, data) {
    const handlers = this.eventHandlers.get(eventName);
    if (!handlers?.length) {
      logger.warn(
        `No handlers registered for event "${eventName}". Did you forget to call .on("${eventName}", handler)?`
      );
      return;
    }
    let validatedData = data;
    if (this.incomingSchema) {
      try {
        validatedData = this.incomingSchema.parse(data);
      } catch (err) {
        if (err instanceof z.ZodError) {
          logger.error(`Invalid incoming event data for "${eventName}":`, {
            errors: err.errors.map((e) => `${e.path.join(".")}: ${e.message}`).join(", "),
            data: JSON.stringify(data, null, 2)
          });
        } else {
          logger.error(`Error validating incoming event "${eventName}":`, err);
        }
        return;
      }
    }
    let hasErrors = false;
    handlers.forEach((handler, index) => {
      try {
        handler(validatedData);
      } catch (err) {
        hasErrors = true;
        const error = err instanceof Error ? err : new Error(String(err));
        logger.error(
          `Error in handler ${index + 1}/${handlers.length} for event "${eventName}":`,
          {
            error: error.message,
            stack: error.stack,
            data: JSON.stringify(validatedData, null, 2)
          }
        );
      }
    });
    if (hasErrors) {
      throw new Error(
        `One or more handlers failed for event "${eventName}". Check logs for details.`
      );
    }
  }
  off(event, callback) {
    if (!callback) {
      this.eventHandlers.delete(event);
    } else {
      const handlers = this.eventHandlers.get(event);
      if (handlers) {
        const index = handlers.indexOf(callback);
        if (index !== -1) {
          handlers.splice(index, 1);
          if (handlers.length === 0) {
            this.eventHandlers.delete(event);
          }
        }
      }
    }
  }
  on(event, callback) {
    if (!callback) {
      logger.error(
        `No callback provided for event handler "${event.toString()}". Ppass a callback to handle this event.`
      );
      return;
    }
    const handlers = this.eventHandlers.get(event) || [];
    handlers.push(callback);
    this.eventHandlers.set(event, handlers);
  }
};

// src/socket.ts
var ServerSocket = class {
  room = "DEFAULT_ROOM";
  ws;
  controllers = /* @__PURE__ */ new Map();
  emitter;
  // private redis: Redis
  redisUrl;
  redisToken;
  lastPingTimes = /* @__PURE__ */ new Map();
  heartbeatTimers = /* @__PURE__ */ new Map();
  constructor(ws, opts) {
    const { incomingSchema, outgoingSchema, redisUrl, redisToken } = opts;
    this.redisUrl = redisUrl;
    this.redisToken = redisToken;
    this.ws = ws;
    this.emitter = new EventEmitter(ws, { incomingSchema, outgoingSchema });
  }
  get rooms() {
    return [this.room];
  }
  close() {
    this.ws.close();
    for (const controller of this.controllers.values()) {
      controller.abort();
    }
    this.controllers.clear();
    for (const timers of this.heartbeatTimers.values()) {
      clearInterval(timers.sender);
      clearInterval(timers.monitor);
    }
    this.heartbeatTimers.clear();
  }
  off(event, callback) {
    return this.emitter.off(event, callback);
  }
  on(event, callback) {
    return this.emitter.on(event, callback);
  }
  emit(event, data) {
    return this.emitter.emit(event, data);
  }
  handleEvent(eventName, eventData) {
    this.emitter.handleEvent(eventName, eventData);
  }
  async join(room) {
    this.room = room;
    logger.info(`Socket trying to join room: "${room}".`);
    await this.subscribe(room).catch((error) => {
      logger.error(`Subscription error for room ${room}:`, error);
    }).then(() => logger.success(`Joined room: ${room}`)).then(() => this.createHeartbeat(room));
  }
  leave(room) {
    const controller = this.controllers.get(room);
    if (controller) {
      controller.abort();
      this.controllers.delete(room);
      logger.info(`Left room: ${room}`);
    } else {
      logger.warn(`Attempted to leave room "${room}" but no active controller found`);
    }
  }
  createHeartbeat(room) {
    const heartbeat = {
      sender: setInterval(async () => {
        await fetch(`${this.redisUrl}/publish/${room}`, {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${this.redisToken}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify(["ping", null])
        });
      }, 3e4),
      monitor: setInterval(() => {
        const lastPingTime = this.lastPingTimes.get(room) ?? 0;
        if (Date.now() - lastPingTime > 45e3) {
          logger.warn("Heartbeat timeout detected");
          this.unsubscribe(room).then(() => this.subscribe(room));
        }
      }, 5e3)
    };
    this.heartbeatTimers.set(room, heartbeat);
  }
  async subscribe(room) {
    return new Promise(async (resolve, reject) => {
      try {
        const controller = new AbortController();
        this.controllers.set(room, controller);
        this.lastPingTimes.set(room, Date.now());
        const stream = await fetch(`${this.redisUrl}/subscribe/${room}`, {
          headers: {
            Authorization: `Bearer ${this.redisToken}`,
            accept: "text/event-stream"
          },
          signal: controller.signal
        });
        const reader = stream.body?.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        while (reader) {
          const { done, value } = await reader.read();
          resolve();
          if (done) break;
          const chunk = decoder.decode(value);
          buffer += chunk;
          const messages = buffer.split("\n");
          buffer = messages.pop() || "";
          for (const message of messages) {
            logger.info("Received message:", message);
            if (message.startsWith("data: ")) {
              const data = message.slice(6);
              try {
                const firstCommaIndex = data.indexOf(",");
                const secondCommaIndex = data.indexOf(",", firstCommaIndex + 1);
                if (firstCommaIndex === -1 || secondCommaIndex === -1) {
                  logger.warn("Invalid message format - missing commas");
                  continue;
                }
                const payloadStr = data.slice(secondCommaIndex + 1);
                if (!payloadStr) {
                  logger.warn("Missing payload in message");
                  continue;
                }
                const parsed = JSON.parse(payloadStr);
                if (parsed[0] === "ping") {
                  logger.success("Heartbeat received successfully");
                  this.lastPingTimes.set(room, Date.now());
                }
                if (this.ws.readyState === WebSocket.OPEN) {
                  this.ws.send(JSON.stringify(parsed));
                } else {
                  logger.debug("WebSocket not open, skipping message");
                }
              } catch (err) {
                logger.debug("Failed to parse message payload", err);
              }
            }
          }
        }
      } catch (err) {
        reject(err);
      }
    });
  }
  async unsubscribe(room) {
    const controller = this.controllers.get(room);
    if (controller) {
      controller.abort();
      this.controllers.delete(room);
      logger.info(`Unsubscribed from room: ${room}`);
    } else {
      logger.warn(`No active subscription found for room: ${room}`);
    }
  }
};
var ClientSocket = class {
  ws;
  emitter;
  url;
  incomingSchema;
  outgoingSchema;
  pingTimer;
  pongTimer;
  reconnectAttempts = 0;
  isConnected = false;
  constructor(url, { incomingSchema, outgoingSchema } = {}) {
    this.url = url;
    this.incomingSchema = incomingSchema;
    this.outgoingSchema = outgoingSchema;
    this.connect();
  }
  cleanup() {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = void 0;
    }
    if (this.pongTimer) {
      clearTimeout(this.pongTimer);
      this.pongTimer = void 0;
    }
  }
  close() {
    this.cleanup();
    if (this.ws.readyState === WebSocket.OPEN) {
      this.ws.close(1e3, "Client closed connection");
    }
    this.isConnected = false;
  }
  connect() {
    const ws = new WebSocket(this.url);
    const existingHandlers = this.emitter?.eventHandlers;
    this.emitter = new EventEmitter(ws, { incomingSchema: this.incomingSchema, outgoingSchema: this.outgoingSchema });
    if (existingHandlers) {
      this.emitter.eventHandlers = new Map(existingHandlers);
    }
    this.ws = ws;
    ws.onerror = (err) => {
      const url = err.currentTarget?.url;
      if (typeof url === "string") {
        if (url.includes(":3000")) {
          console.error(`\u{1F6A8} Cannot connect to WebSocket server

WebSocket connections require Cloudflare Workers (port 8080).
Node.js servers (port 3000) do not support WebSocket connections.

To start your Cloudflare Worker locally:
$ wrangler dev

Fix this issue: https://jstack.app/docs/getting-started/local-development
          `);
        }
      } else {
        logger.error(`Connection error:`, err);
      }
      this.emitter.handleEvent("onError", err);
    };
    ws.onopen = () => {
      logger.success("Connected");
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.emitter.handleEvent("onConnect", void 0);
    };
    ws.onclose = () => {
      logger.warn(`Connection closed, trying to reconnect...`);
      this.isConnected = false;
      this.reconnectAttempts++;
      if (this.reconnectAttempts < 3) {
        setTimeout(() => this.connect(), 1500);
      } else {
        logger.error(
          "Failed to establish connection after multiple attempts. Check your network connection, or refresh the page to try again."
        );
      }
    };
    ws.onmessage = (event) => {
      const data = z2.string().parse(event.data);
      const eventSchema = z2.tuple([z2.string(), z2.unknown()]);
      const parsedData = JSON.parse(data);
      const parseResult = eventSchema.safeParse(parsedData);
      if (parseResult.success) {
        const [eventName, eventData] = parseResult.data;
        this.emitter.handleEvent(eventName, eventData);
      } else {
        logger.warn("Unable to parse event:", event.data);
      }
    };
  }
  emit(event, data) {
    return this.emitter.emit(event, data);
  }
  off(event, callback) {
    return this.emitter.off(event, callback);
  }
  on(event, callback) {
    return this.emitter.on(event, callback);
  }
};
export {
  ClientSocket,
  ServerSocket,
  logger
};
